import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tab,
  TabList,
  Tabs,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { ActionButtons } from '@/components/common/ActionButtons'
import { DataTable, type DataTableOptions, type DataTableRef } from '@/components/tables'
import { useAccessCheck } from '@/hooks/useAccess'
import { useThemeColors } from '@/hooks/useThemeColors'
import { fetchClientOptionsList } from '@/lib/services/clientContextService'
import { importPurchaseOrders } from '@/lib/services/clientPurchaseOrderService'
import { apiClient } from '@/lib/api/client'
import { exportToCSV, downloadCSVTemplate } from '@/lib/utils/csvUtils'
import {
  CLIENT_PO_CSV_HEADERS,
  CLIENT_PO_CSV_TEMPLATE_EXAMPLE_ROW,
  mapPurchaseOrderToCsvRow,
} from '@/lib/utils/clientPurchaseOrderCsv'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type TabValue = 'all' | 'trash'

const formatDate = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—'
  return String(value).slice(0, 10)
}

const formatAmount = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return '—'
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

export default function ClientPurchaseOrdersPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const access = useAccessCheck()
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [bulkAction, setBulkAction] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [clientOptions, setClientOptions] = useState<Array<{ id: number; code: string; name: string }>>([])

  useEffect(() => {
    fetchClientOptionsList()
      .then((clients) => {
        setClientOptions(clients.map((c) => ({ id: c.id, code: c.code, name: c.name })))
      })
      .catch(() => setClientOptions([]))
  }, [])

  const statusFilterOptions = useMemo(
    () => [
      { value: 'active', label: 'Active' },
      { value: 'expiring', label: 'Expiring Soon' },
      { value: 'expired', label: 'Expired' },
    ],
    []
  )

  const customRenderers = useMemo(
    () => ({
      client: (_value: unknown, row?: Record<string, unknown>): ReactNode => {
        const client = row?.client as { name?: string; code?: string } | undefined
        if (!client?.name) return '—'
        return client.code ? `${client.name} (${client.code})` : client.name
      },
      po_date: (value: unknown) => formatDate(value),
      start_date: (value: unknown) => formatDate(value),
      end_date: (value: unknown) => formatDate(value),
      amount: (value: unknown) => formatAmount(value),
      is_active: (value: unknown, row?: Record<string, unknown>) => {
        const expiring = row?.is_expiring_soon
        if (value === false) {
          return (
            <Badge colorScheme="red" fontSize="0.7rem">
              Expired
            </Badge>
          )
        }
        if (expiring) {
          return (
            <Badge colorScheme="orange" fontSize="0.7rem">
              Expiring Soon
            </Badge>
          )
        }
        return (
          <Badge colorScheme="green" fontSize="0.7rem">
            Active
          </Badge>
        )
      },
    }),
    []
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        po_number: { name: 'PO No.', withSort: true, maxWidth: '140px' },
        client: { name: 'Client', withSort: false, maxWidth: '200px' },
        po_date: { name: 'PO Date', withSort: true, maxWidth: '110px' },
        start_date: { name: 'Start', withSort: true, maxWidth: '110px' },
        end_date: { name: 'End', withSort: true, maxWidth: '110px' },
        amount: { name: 'Amount', withSort: false, maxWidth: '110px' },
        portfolio: {
          name: 'Portfolio',
          withSort: true,
          maxWidth: '160px',
          render: (value: unknown) => (value ? String(value) : '—'),
        },
        sales_person: {
          name: 'Sales Person',
          withSort: true,
          maxWidth: '140px',
          render: (value: unknown) => (value ? String(value) : '—'),
        },
        is_active: { name: 'Status', withSort: false, maxWidth: '120px' },
        updated_at: { name: 'Updated At', withSort: true, maxWidth: '150px' },
      },
      dataSource:
        activeTab === 'trash'
          ? '/organization/client-purchase-orders/trashed'
          : '/organization/client-purchase-orders',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No purchase orders found',
    }),
    [activeTab]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      ...(clientFilter ? { client_id: clientFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    [searchQuery, clientFilter, statusFilter]
  )

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) searchRef.current.value = ''
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    setModalMessage(
      bulkAction === 'delete' && activeTab === 'trash'
        ? 'You are about to permanently delete these purchase orders. This action cannot be undone.'
        : 'Are you sure you want to apply this change?'
    )
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url = ''
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/organization/client-purchase-orders/bulk/restore'
        successMessage = 'Purchase orders restored successfully'
      } else if (bulkAction === 'delete') {
        if (activeTab === 'trash') {
          await Promise.all(
            selectedIds.map((id) => apiClient.delete(`/organization/client-purchase-orders/${id}/force`))
          )
          showSuccess('Success', 'Purchase orders permanently deleted successfully')
          tableRef.current?.reload()
          setBulkAction('')
          onClose()
          return
        }
        url = '/organization/client-purchase-orders/bulk/delete'
        successMessage = 'Purchase orders moved to trash successfully'
      } else {
        return
      }

      await apiClient.post(url, { ids: selectedIds })
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleDownloadTemplate = () => {
    downloadCSVTemplate({
      csvHeaders: [...CLIENT_PO_CSV_HEADERS],
      exampleRow: CLIENT_PO_CSV_TEMPLATE_EXAMPLE_ROW,
      filename: 'client_purchase_orders_template.csv',
      comments: [
        'Leave ID blank for new purchase orders; include ID when re-importing exported rows.',
        'Client Name, Status, Created At, and Updated At are ignored on import.',
      ],
      showError,
      showSuccess,
    })
  }

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsImporting(true)
    try {
      const result = await importPurchaseOrders(file)
      const parts: string[] = []
      if (result.imported > 0) parts.push(`Created ${result.imported}`)
      if (result.updated > 0) parts.push(`Updated ${result.updated}`)
      showSuccess('Success', parts.length > 0 ? `${parts.join(', ')} purchase order(s)` : 'Import completed')
      if (result.errors?.length) {
        showWarning(result.errors.slice(0, 3).join('; ') + (result.errors.length > 3 ? '...' : ''))
      }
      tableRef.current?.reload()
    } catch (err: unknown) {
      showError(err)
    } finally {
      setIsImporting(false)
      e.target.value = ''
    }
  }

  const handleExportData = async () => {
    setIsExporting(true)
    try {
      await exportToCSV({
        endpoint: '/organization/client-purchase-orders',
        selectedIds: [],
        activeTab,
        searchQuery: '',
        csvHeaders: [...CLIENT_PO_CSV_HEADERS],
        dataMapper: (po: Record<string, unknown>) => mapPurchaseOrderToCsvRow(po),
        showError,
        showSuccess,
      })
    } catch (error: any) {
      showError(error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Client Purchase Orders
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage purchase orders across client tenants
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/organization/client-purchase-orders"
                      onExport={handleExportData}
                      onDownloadTemplate={handleDownloadTemplate}
                      createLabel="Create Purchase Order"
                      isExporting={isExporting}
                      isImporting={isImporting}
                      fileInputRef={fileInputRef}
                      onFileUpload={handleFileUpload}
                      acceptFileTypes=".csv"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e) => setBulkAction(e.target.value)}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                <Select
                  placeholder="All clients"
                  size="sm"
                  maxW="220px"
                  value={clientFilter}
                  onChange={(e) => {
                    setClientFilter(e.target.value)
                    tableRef.current?.clearPage()
                  }}
                >
                  {clientOptions.map((client) => (
                    <option key={client.id} value={String(client.id)}>
                      {client.name} ({client.code})
                    </option>
                  ))}
                </Select>

                <Select
                  placeholder="All statuses"
                  size="sm"
                  maxW="180px"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value)
                    tableRef.current?.clearPage()
                  }}
                >
                  {statusFilterOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search PO number, service area, portfolio..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  customRenderers={customRenderers}
                  onOpenModal={(id) => navigate(`/organization/client-purchase-orders/${id}`)}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmBulkAction}
              isLoading={isBulkProcessing}
              loadingText="Processing..."
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
